var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localized } from '@lit/localize';
import { CommonActions } from '../../localization/helpers.js';
import '../icon/Icon.js';
import '../button/SimpleButton.js';
/**
 * Generic, fully-parametrable information / confirmation modal.
 *
 * One info-style accent icon (default `info`, tintable), a heading, a body
 * (the `message` prop or, when empty, the default `<slot>` for rich content),
 * and one or two actions. Emits `confirm` when the primary button is clicked
 * and `cancel` on the secondary button or a backdrop dismiss. The host owns
 * the DOM lifecycle (append to `<body>`, remove on settle) — see the
 * promise-based wrappers consumers usually layer on top.
 *
 * Everything is a property so the same element covers acknowledge-only
 * dialogs (`showCancel = false`), destructive confirms (`confirmVariant =
 * 'danger'`), and neutral "do you want to update?" prompts (the default).
 */
let InformationModal = class InformationModal extends LitElement {
    constructor() {
        super(...arguments);
        /** Modal title. */
        this.heading = '';
        /** Body text. When empty, the default `<slot>` is rendered instead so the
         *  caller can project arbitrary content. */
        this.message = '';
        /** Accent icon name (any `basic-icon` name). Empty string hides the icon. */
        this.iconName = 'info';
        /** Icon foreground colour (the icon is tintable via `currentColor`). */
        this.iconColor = '#1d4ed8';
        /** Icon disc background colour. */
        this.iconBackground = '#eff6ff';
        /** Primary (confirm) button label. */
        this.confirmLabel = CommonActions.confirm();
        /** Secondary (cancel) button label. */
        this.cancelLabel = CommonActions.cancel();
        /** Primary button variant. */
        this.confirmVariant = 'primary';
        /** Whether the primary button uses the gradient `special` style. */
        this.confirmSpecial = true;
        /** Show the secondary (cancel) button. Set false for acknowledge-only. */
        this.showCancel = true;
        /** Clicking the dimmed backdrop dismisses the modal (emits `cancel`). */
        this.dismissOnBackdrop = true;
        /** A click that reaches `:host` (not stopped by `.container`) is a backdrop
         *  click — dismiss as a cancel when enabled. */
        this._onBackdrop = (e) => {
            if (!this.dismissOnBackdrop)
                return;
            if (e.target === this)
                this._onCancel();
        };
    }
    render() {
        return html `
      <div
        class="container"
        role="dialog"
        aria-modal="true"
        @click=${(e) => e.stopPropagation()}
      >
        ${this.iconName
            ? html `<div
              class="icon-disc"
              style=${`background:${this.iconBackground};color:${this.iconColor};`}
            >
              <basic-icon name=${this.iconName} size="22"></basic-icon>
            </div>`
            : nothing}
        ${this.heading ? html `<h2>${this.heading}</h2>` : nothing}
        <div class="body">
          ${this.message ? this.message : html `<slot></slot>`}
        </div>
        <div class="button-group">
          <simple-button
            variant=${this.confirmVariant}
            ?special=${this.confirmSpecial}
            label=${this.confirmLabel}
            @button-click=${this._onConfirm}
          ></simple-button>
          ${this.showCancel
            ? html `<simple-button
                variant="secondary"
                label=${this.cancelLabel}
                @button-click=${this._onCancel}
              ></simple-button>`
            : nothing}
        </div>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this._onBackdrop);
    }
    disconnectedCallback() {
        this.removeEventListener('click', this._onBackdrop);
        super.disconnectedCallback();
    }
    _onConfirm() {
        this.dispatchEvent(new CustomEvent('confirm', { bubbles: true, composed: true }));
    }
    _onCancel() {
        this.dispatchEvent(new CustomEvent('cancel', { bubbles: true, composed: true }));
    }
};
InformationModal.styles = css `
    :host {
      position: fixed;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 1000;
    }
    .container {
      background: #fff;
      padding: 22px 26px;
      border-radius: 10px;
      box-shadow: 0 4px 18px rgba(0, 0, 0, 0.2);
      max-width: 360px;
      text-align: center;
    }
    .icon-disc {
      width: 44px;
      height: 44px;
      margin: 0 auto 14px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .icon-disc basic-icon {
      color: inherit;
    }
    h2 {
      margin: 0 0 10px;
      font-size: 16px;
      color: #1f2937;
    }
    .body {
      margin: 0 0 20px;
      font-size: 13.5px;
      line-height: 1.45;
      color: #4b5563;
    }
    .button-group {
      display: flex;
      justify-content: center;
      gap: 12px;
    }
  `;
__decorate([
    property({ type: String })
], InformationModal.prototype, "heading", void 0);
__decorate([
    property({ type: String })
], InformationModal.prototype, "message", void 0);
__decorate([
    property({ type: String })
], InformationModal.prototype, "iconName", void 0);
__decorate([
    property({ type: String })
], InformationModal.prototype, "iconColor", void 0);
__decorate([
    property({ type: String })
], InformationModal.prototype, "iconBackground", void 0);
__decorate([
    property({ type: String })
], InformationModal.prototype, "confirmLabel", void 0);
__decorate([
    property({ type: String })
], InformationModal.prototype, "cancelLabel", void 0);
__decorate([
    property({ type: String })
], InformationModal.prototype, "confirmVariant", void 0);
__decorate([
    property({ type: Boolean })
], InformationModal.prototype, "confirmSpecial", void 0);
__decorate([
    property({ type: Boolean })
], InformationModal.prototype, "showCancel", void 0);
__decorate([
    property({ type: Boolean })
], InformationModal.prototype, "dismissOnBackdrop", void 0);
InformationModal = __decorate([
    localized(),
    customElement('information-modal')
], InformationModal);
export { InformationModal };
//# sourceMappingURL=InformationModal.js.map